/**
 * Mise en page commune : barre supérieure, navigation et zone de contenu.
 *
 * Le lien « Administration » n'apparaît que pour le rôle `admin` ; les routes
 * `/admin/*` restent malgré tout gardées côté composant, l'affichage n'est
 * jamais un contrôle d'accès.
 */

import clsx from 'clsx';
import { LogOut, Shield } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { logout, resetCsrfToken } from '../api';
import { Backdrop } from './Backdrop';
import { useSession } from '../hooks/useSession';
import { messageOf } from '../hooks/useAsync';
import { ThemeToggle } from './ThemeToggle';
import { Button, FOCUS_RING } from './ui';

/** Onglets principaux du portail. */
const MAIN_LINKS = [
  { to: '/', label: 'Déployer', end: true },
  { to: '/mes-applications', label: 'Mes applications', end: false },
];

/** Style commun aux liens de navigation. */
function navClass({ isActive }: { isActive: boolean }): string {
  return clsx(
    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
    FOCUS_RING,
    isActive
      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
  );
}

/** Barre supérieure. */
function TopBar() {
  const { user } = useSession();
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onLogout = (): void => {
    setSigningOut(true);
    setError(null);
    logout()
      .then(() => {
        resetCsrfToken();
        // Rechargement complet : toute la mémoire de la session est purgée,
        // et la page suivante repassera par l'authentification OIDC.
        window.location.assign('/');
      })
      .catch((cause: unknown) => {
        setError(messageOf(cause));
        setSigningOut(false);
      });
  };

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-3 px-4 py-3 sm:px-6">
        <NavLink
          to="/"
          className={clsx(
            'flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-slate-50',
            FOCUS_RING,
          )}
        >
          {/*
            Le pictogramme de la charte remplace l'icône générique. Deux fichiers
            plutôt qu'un filtre CSS : la version blanche est fournie par la charte,
            et la teinter à la main dénaturerait les trois pastilles de couleur.
            L'échange est fait par les classes de thème, sans JavaScript, comme le
            reste de l'interface (`darkMode: 'media'`).
          */}
          <img
            src="/marque/odice-mark.png"
            alt=""
            aria-hidden="true"
            className="h-7 w-auto dark:hidden"
          />
          <img
            src="/marque/odice-mark-blanc.png"
            alt=""
            aria-hidden="true"
            className="hidden h-7 w-auto dark:block"
          />
          Portail de déploiement
        </NavLink>

        <nav aria-label="Navigation principale" className="flex items-center gap-1">
          {MAIN_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.end} className={navClass}>
              {link.label}
            </NavLink>
          ))}
          {user.role === 'admin' ? (
            <NavLink to="/admin" className={navClass}>
              <span className="inline-flex items-center gap-1.5">
                <Shield className="h-4 w-4" aria-hidden="true" />
                Administration
              </span>
            </NavLink>
          ) : null}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <span className="hidden text-right text-sm leading-tight sm:block">
            <span className="block font-medium text-slate-800 dark:text-slate-200">
              {user.displayName}
            </span>
            <span className="block text-xs text-slate-500 dark:text-slate-400">{user.email}</span>
          </span>
          <ThemeToggle />
          <Button variant="ghost" onClick={onLogout} loading={signingOut}>
            <LogOut className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Déconnexion</span>
          </Button>
        </div>
      </div>

      {error ? (
        <p
          role="alert"
          className="bg-red-50 px-4 py-2 text-sm text-red-800 dark:bg-red-950 dark:text-red-200"
        >
          {error}
        </p>
      ) : null}
    </header>
  );
}

/** Gabarit de toutes les pages du portail. */
export function Layout() {
  // Sans couleur de fond propre : le dégradé posé par `body::before` doit rester
  // visible à travers le gabarit.
  return (
    <div className="min-h-screen text-slate-900 dark:text-slate-100">
      <Backdrop />
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-indigo-600 focus:px-3 focus:py-2 focus:text-white"
      >
        Aller au contenu
      </a>
      <TopBar />
      <main id="contenu" className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <Outlet />
      </main>

      {/*
        Pied de page discret : le logo complet situe l'outil dans le groupe, là où
        l'en-tête ne porte que la marque et le nom du service. Volontairement en
        bas et en petit — c'est un outil interne, pas une vitrine.
      */}
      <footer className="mx-auto max-w-7xl px-4 pb-8 sm:px-6">
        <div className="flex items-center gap-3 border-t border-slate-200 pt-6 dark:border-slate-800">
          <img
            src="/marque/odice-logo.png"
            alt="Groupe Odice"
            className="h-8 w-auto opacity-70 dark:hidden"
          />
          <img
            src="/marque/odice-logo-blanc.png"
            alt="Groupe Odice"
            className="hidden h-8 w-auto opacity-70 dark:block"
          />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Outil interne du Groupe Odice — hébergé sur l’infrastructure de l’entreprise.
          </p>
        </div>
      </footer>
    </div>
  );
}
